import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Eye, EyeOff, LogIn } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const next = loc.state?.from || "/";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav(next, { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Logo />
          <div className="mt-10">
            <div className="lx-overline mb-3">Acceso al sistema</div>
            <h1 className="text-4xl font-heading font-bold tracking-tight" data-testid="login-title">
              Bienvenido de nuevo
            </h1>
            <p className="mt-2 text-lx-muted">
              Ingresa para gestionar las actividades de limpieza, sucursales y QRs.
            </p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="lx-label" htmlFor="email">Correo electrónico</label>
              <input
                id="email"
                type="email"
                className="lx-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@limpioexpress.com"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="lx-label" htmlFor="password">Contraseña</label>
              <div className="relative">
                <input
                  id="password"
                  type={show ? "text" : "password"}
                  className="lx-input pr-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-lx-muted hover:text-lx-text"
                  data-testid="toggle-password-visibility"
                  aria-label="Mostrar/ocultar contraseña"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {err && (
              <div className="text-sm rounded-lg px-3 py-2.5 bg-red-50 text-red-700 border border-red-200" data-testid="login-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="lx-btn lx-btn-primary w-full h-11"
              data-testid="login-submit-button"
            >
              {loading ? "Ingresando…" : (<><LogIn size={16}/> Entrar</>)}
            </button>

            <div className="text-xs text-lx-muted text-center pt-2">
              ¿Olvidaste tu contraseña? Contacta al administrador.
            </div>
          </form>
        </div>
      </div>

      {/* Right: brand panel */}
      <div className="hidden lg:block relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url(https://images.unsplash.com/photo-1603712725038-e9334ae8f39f?crop=entropy&cs=srgb&fm=jpg&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(0,174,239,0.85), rgba(0,140,190,0.92))" }}/>
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="lx-overline" style={{ color: "rgba(255,255,255,0.85)" }}>Operación · Control · Confianza</div>
          <div>
            <h2 className="font-heading font-bold text-5xl tracking-tight leading-[1.05]">
              La limpieza,<br/>verificada en segundos.
            </h2>
            <p className="mt-4 text-white/85 text-lg max-w-md">
              Escanea el QR, registra la actividad, recibe reportes en tiempo real. Limpio Express simplifica la supervisión operativa.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-6 max-w-md">
            {[
              { k: "100%", v: "Trazabilidad" },
              { k: "<10s", v: "Por check" },
              { k: "24/7", v: "Monitoreo" },
            ].map((s) => (
              <div key={s.k}>
                <div className="font-heading text-3xl font-bold">{s.k}</div>
                <div className="text-xs uppercase tracking-widest text-white/80 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
